import { hasPermission } from "@avela/avela-authorization-sdk";
import {
  Box,
  Center,
  Divider,
  Flex,
  List,
  ListItem,
  Skeleton,
  Spacer,
  VStack,
} from "@chakra-ui/react";
import _ from "lodash";
import { useMemo } from "react";
import * as BoundaryService from "src/components/Boundary/services";
import { useAuthorizationScoped } from "src/components/Providers/AuthorizationProvider";
import {
  FORM_SCHOOL_RANK_SUB_STATUS,
  FormSchoolRankSubStatusIndex,
} from "src/constants";
import {
  useOrganization,
  UseOrganizationType,
  useTimezoneName,
} from "src/hooks/useOrganization";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import { SingleMoreMenu } from "src/scenes/orgAdmin/forms/components/SingleMoreMenu";
import { calculateOfferRemainingTime } from "src/services/form/offer";
import { formatIsoDateToOrgDateTime } from "src/services/format";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import { SchoolRankWithTags } from "src/types/schoolRank";
import { GenericError } from "../Feedback/GenericError";
import { SchoolListItem } from "../Inputs/MultiSelectSchoolRank/SchoolListItem";
import { RemoteDataView } from "../Layout/RemoteDataView";
import { WithRequiredHasuraRoles } from "../Permissions/WithRequiredHasuraRoles";
import { WithUserPermissions } from "../Permissions/WithUserPermissions";
import { TagGroups } from "../Tags/TagGroups/TagGroups";

type Props = {
  formId: uuid;
  enrollmentPeriodId: uuid;
  schoolsRank: SchoolRankWithTags[];
  onRefetchSchoolsRank?: () => Promise<void>;
  onRefetchFormAlerts?: () => Promise<void>;
  tagGroups?: GQL.GetSchoolsRankView_tag_group[];
  isOfferEnabled?: boolean;
  rankingEnabled: boolean;
  onOffersAccepted?: () => void;
};
export const SchoolRanking: React.FC<Props> = ({
  formId,
  enrollmentPeriodId,
  schoolsRank,
  onRefetchSchoolsRank,
  onRefetchFormAlerts,
  tagGroups,
  isOfferEnabled = true,
  rankingEnabled,
  onOffersAccepted,
}) => {
  const isAdmin = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.DISTRICT_ADMIN,
    HasuraRole.ORG_ADMIN,
    HasuraRole.SCHOOL_ADMIN,
  ]);

  const { withBoundaryTags } = BoundaryService.useBoundaryTags<
    SchoolRankWithTags["school"]
  >({
    formId,
  });

  const authorizationMap = useAuthorizationScoped("permissions");
  const timezoneName = useTimezoneName();
  const organization = useOrganization();

  const hasOfferReadPermission = useMemo(() => {
    if (!authorizationMap.hasData()) {
      return false;
    }

    return hasPermission({
      action: {
        type: "Perform::Action",
        id: "with_permission",
      },
      resource: {
        type: "Permission",
        id: "offer:read",
      },
      authorizationMap: authorizationMap.data,
    });
  }, [authorizationMap]);

  const rankedSchoolsWithBoundaryTagsRemoteData = useMemo(() => {
    const list = schoolsRank.map((rankedSchool) => {
      const rd = withBoundaryTags(rankedSchool.school);
      return rd.map((schoolWithBoundaryTags) => ({
        ...rankedSchool,
        school: schoolWithBoundaryTags,
      }));
    });
    return RD.combine(list);
  }, [schoolsRank, withBoundaryTags]);

  const renderSelectItem = (schoolRank: SchoolRankWithTags) => (
    <SchoolListItem
      school={schoolRank.school}
      subStatus={
        hasOfferReadPermission
          ? getSubstatus(schoolRank, isAdmin, timezoneName)
          : undefined
      }
      offerExpiresAt={
        hasOfferReadPermission
          ? getOfferExpiresAt(schoolRank, isAdmin, organization)
          : undefined
      }
      direction="row"
    />
  );

  return (
    <List
      styleType={rankingEnabled || isAdmin ? "none" : "disc"}
      marginLeft="0"
      display="flex"
      flexDirection="column"
      gap={3}
      fontSize="sm"
      position="relative"
    >
      <RemoteDataView
        error={(error) => <GenericError error={error} />}
        loading={<Loading />}
        remoteData={rankedSchoolsWithBoundaryTagsRemoteData}
      >
        {(rankedSchoolsWithBoundaryTags) => (
          <>
            {_.sortBy(
              rankedSchoolsWithBoundaryTags,
              (schoolRank) => schoolRank.rank
            ).map((schoolRank, idx) => {
              const hasRank =
                rankingEnabled && typeof schoolRank.rank === "number";

              const selectedFormSchoolsRD = RD.success([
                {
                  formId: schoolRank.form.id,
                  schoolId: schoolRank.school.id,
                  formSchoolRankId: schoolRank.id,
                },
              ]);

              const showDivider =
                (rankingEnabled || isAdmin) && idx < schoolsRank.length - 1;

              let ml: number;
              if (rankingEnabled) {
                ml = 2;
              } else {
                // ranking disabled
                if (isAdmin) {
                  ml = 0;
                } else {
                  ml = 4;
                }
              }

              return (
                <ListItem
                  key={schoolRank.school.id}
                  gap={3}
                  alignItems="center"
                  flexWrap="wrap"
                  ml={ml}
                  mr={2}
                >
                  <VStack alignItems="initial" width="100%">
                    <Flex direction="row" gap="2">
                      {hasRank && (
                        <>
                          <Box as="span">{schoolRank.rank + 1}</Box>
                          <Center height="5">
                            <Divider orientation="vertical" />
                          </Center>
                        </>
                      )}
                      {renderSelectItem(schoolRank)}
                      <Spacer />
                      <Box>
                        <WithRequiredHasuraRoles
                          roles={[
                            HasuraRole.ADMIN,
                            HasuraRole.DISTRICT_ADMIN,
                            HasuraRole.ORG_ADMIN,
                            HasuraRole.SCHOOL_ADMIN,
                          ]}
                        >
                          <WithUserPermissions permissions={["offer:update"]}>
                            {isOfferEnabled && (
                              <SingleMoreMenu
                                selectedFormSchoolsRD={selectedFormSchoolsRD}
                                onRefetch={async () => {
                                  await onRefetchSchoolsRank?.();
                                  await onRefetchFormAlerts?.();
                                }}
                                onOffersAccepted={onOffersAccepted}
                              />
                            )}
                          </WithUserPermissions>
                        </WithRequiredHasuraRoles>
                      </Box>
                    </Flex>
                    {tagGroups && "tags" in schoolRank && (
                      <TagGroups
                        schoolRank={schoolRank}
                        tagGroups={tagGroups}
                        enrollmentPeriodId={enrollmentPeriodId}
                      />
                    )}
                    {showDivider && <Divider orientation="horizontal" />}
                  </VStack>
                </ListItem>
              );
            })}
          </>
        )}
      </RemoteDataView>
    </List>
  );
};

function getSubstatus(
  schoolRank: SchoolRankWithTags,
  isAdmin: boolean,
  timezoneName: string
) {
  if (!schoolRank.sub_status) {
    return undefined;
  }

  const subStatus =
    FORM_SCHOOL_RANK_SUB_STATUS[
      schoolRank.sub_status as FormSchoolRankSubStatusIndex
    ];

  const label = getSubstatusLabel(schoolRank, isAdmin, timezoneName);

  return label ? { ...subStatus, label, longLabel: label } : subStatus;
}

function getOfferExpiresAt(
  schoolRank: SchoolRankWithTags,
  isAdmin: boolean,
  organization: UseOrganizationType
): string | undefined {
  const expiresAt = schoolRank.offers[0]?.expires_at
    ? formatIsoDateToOrgDateTime(schoolRank.offers[0].expires_at, organization)
    : undefined;

  return isAdmin && schoolRank.sub_status === "Offered" && expiresAt
    ? expiresAt
    : undefined;
}

// This is for adding extra information to the substatus tag, including waitlist
// position and offer programs.
const getSubstatusLabel = (
  schoolRank: SchoolRankWithTags,
  isAdmin: boolean,
  timezoneName: string
) => {
  if (isAdmin && schoolRank.offers[0]) {
    const offer = schoolRank.offers[0];
    const labelParts: string[] = [offer.status];

    if ("form_school_grades" in schoolRank && "bin_id" in offer) {
      const offerProgram = schoolRank.form_school_grades.find(
        (grade) => grade.bin_id === offer.bin_id
      )?.grade?.program?.label;
      if (offerProgram) {
        labelParts.push(offerProgram);
      }
    }

    if (offer.status === GQL.offer_status_enum.Offered && offer.expires_at) {
      const remainingTime = offer.expires_at
        ? calculateOfferRemainingTime({
            expires_at: offer.expires_at,
            timezone_name: timezoneName,
          })
        : undefined;
      if (remainingTime !== undefined) {
        labelParts.push(`${remainingTime}d left`);
      }
    }

    return labelParts.join(", ");
  }
  const waitlistPosition = schoolRank.waitlists[0]?.waitlist_position?.position;
  if (
    !schoolRank.offers[0] &&
    schoolRank.waitlists[0]?.status === GQL.waitlist_status_enum.Waitlisted &&
    waitlistPosition
  ) {
    return `${GQL.waitlist_status_enum.Waitlisted}, #${waitlistPosition}`;
  }
  return undefined;
};

export const Loading = () => (
  <Flex width="100%" maxWidth="30rem" direction="column" padding="2rem" gap="6">
    <Flex border="px solid red" height="1rem" width="100%" paddingX="2rem">
      <Skeleton height="1rem" width="100%" borderRadius="md" />
    </Flex>
    <Flex direction="column" gap="3" alignItems="center">
      <Skeleton height="2rem" width="100%" borderRadius="md" />
      <Skeleton height="2rem" width="100%" borderRadius="md" />
      <Skeleton height="2rem" width="100%" borderRadius="md" />
    </Flex>
  </Flex>
);
